import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  DatePicker,
  Select,
  Checkbox,
  Button,
  Divider,
  Typography,
  Popover,
  Space,
  message,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  IndexFund,
  RefreshIndexFund,
  refreshIndexFundDataFromRefinitiv,
  uploadIndexFund,
  selectors,
} from 'state';

const { Text } = Typography;

type Props = {
  open: boolean;
  fund: IndexFund | null; // when set, the Refinitiv pull is scoped (locked) to this fund
  onClose: () => void;
};

const csvInfo = (
  <div>
    <p>timestamp,close,ric,delete_prev_data</p>
    <p>where timestamp format is: 06-28-2022</p>
    <p>and delete_prev_data: True / False</p>
  </div>
);

/**
 * The two EoD-data operations, cleanly separated: a Refinitiv date-range pull and a CSV history
 * upload. Replaces the classic RefreshIndexFundModal (which crammed both together with leftover
 * `news_file` cruft). When opened from a fund's drawer, the fund is pre-selected and locked.
 */
const DataOpsModal: React.FC<Props> = ({ open, fund, onClose }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const funds = useSelector(selectors.indexFundsList);
  const waiting = useSelector(selectors.waiting);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (fund) form.setFieldsValue({ index_fund_id: fund.id });
    setFile(null);
  }, [open, fund, form]);

  const pull = () => {
    const v = form.getFieldsValue();
    if (!v.from_date || !v.to_date || !v.index_fund_id) {
      message.error('From, To and Index fund are required.');
      return;
    }
    const payload: RefreshIndexFund = {
      from_date: v.from_date,
      to_date: v.to_date,
      index_fund_id: v.index_fund_id,
      delete_prev_data: !!v.delete_prev_data,
    };
    dispatch(refreshIndexFundDataFromRefinitiv(payload));
  };

  const readFileAsText = (f: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(f);
    });

  const upload = async () => {
    if (!file) return;
    const content = await readFileAsText(file);
    dispatch(uploadIndexFund({ index_fund_file: content }));
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={fund ? `EoD data · ${fund.ric}` : 'Import / refresh EoD data'}
      footer={null}
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ delete_prev_data: false }}
      >
        <Text strong>Pull from Refinitiv</Text>
        <Form.Item
          label="From"
          name="from_date"
          rules={[{ required: true, message: 'required' }]}
          style={{ marginTop: 8 }}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="To"
          name="to_date"
          rules={[{ required: true, message: 'required' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="Index fund"
          name="index_fund_id"
          rules={[{ required: true, message: 'required' }]}
        >
          <Select
            showSearch
            allowClear
            optionFilterProp="children"
            disabled={!!fund}
          >
            {funds.map(f => (
              <Select.Option key={f.id} value={f.id}>
                {f.ric}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="delete_prev_data" valuePropName="checked">
          <Checkbox>Delete previous data first</Checkbox>
        </Form.Item>
        <Button loading={waiting} onClick={pull}>
          Pull new data from Refinitiv
        </Button>
        {waiting && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              This may take a while — you can safely close this modal.
            </Text>
          </div>
        )}
      </Form>

      <Divider />

      <Space align="center">
        <Text strong>Upload CSV history</Text>
        <Popover
          placement="right"
          content={csvInfo}
          title=".csv header must look like:"
        >
          <InfoCircleOutlined />
        </Popover>
      </Space>
      <div style={{ marginTop: 8 }}>
        <input
          type="file"
          accept=".csv"
          onChange={e => setFile(e.target.files?.[0] ?? null)}
        />
        <Button disabled={!file} style={{ marginLeft: 8 }} onClick={upload}>
          Upload
        </Button>
      </div>
    </Modal>
  );
};

export default DataOpsModal;
